import React, { useState } from 'react';
import Header from '../../components/common/global-top-bar';
import { CustomInput } from '../../components/common';
import SwitchBar from '../../components/switch-bar';
import { HeaderOptions } from './clients-constants';
import HeadBack from './redirect';
import Spinner from '../../components/common/spinner';
import { toast } from 'react-toastify';
import moment from 'moment';
import Modal from '../../components/common/modal';
import {
  useClientAdministrators,
  useDeleteClientAdministrator,
  useInviteClientAdministrator,
  useResendClientAdministratorInvite,
  useRevokeClientAdministratorInvite,
} from '../../hooks/useClientAdministrators';
import { useClient } from '../../hooks/useClients';

interface ModalProps {
  firstName: string;
  id: string;
  emailAddress: string;
  type: string;
}

const Admin: React.FunctionComponent = (): JSX.Element => {
  const clientId = localStorage.getItem('clientid');
  const { data: clientAdministrators, isLoading: clientAdministratorsLoading } = useClientAdministrators(clientId!);
  const { data: clientDetails, isLoading: clientDetailsLoading } = useClient(Number(clientId!));
  const { inviteClientAdministrator } = useInviteClientAdministrator(clientId!);
  const { deleteClientAdministrator } = useDeleteClientAdministrator(clientId!);
  const { resendClientAdministratorInvite } = useResendClientAdministratorInvite(clientId!);
  const { revokeClientAdministratorInvite } = useRevokeClientAdministratorInvite(clientId!);
  const [emailAddress, setEmailAddress] = useState('');
  const [modal, setModal] = useState<ModalProps | null>(null);

  const handleAlert = (payload: ModalProps) => {
    const { id, emailAddress, type } = payload;
    if (type === 'remove') {
      deleteClientAdministrator.mutate(
        { id, emailAddress },
        {
          onSuccess: () => {
            setModal(null);
          },
        },
      );
    } else {
      revokeClientAdministratorInvite.mutate(
        { userId: id, emailAddress },
        {
          onSuccess: () => {
            setModal(null);
          },
        },
      );
    }
  };

  const resendLink = (id: any) => {
    resendClientAdministratorInvite.mutate(id);
  };

  const sendInvite = () => {
    const trimEmailAddress = emailAddress?.trim();
    setEmailAddress(trimEmailAddress);
    const re = /\S+@\S+\.\S+/;
    if (trimEmailAddress?.length && re.test(trimEmailAddress)) {
      inviteClientAdministrator.mutate(trimEmailAddress, {
        onSuccess: () => {
          setEmailAddress('');
        },
      });
    } else {
      if (!trimEmailAddress) {
        toast.error('Please enter mail address');
      } else if (!re.test(trimEmailAddress)) {
        toast.error('Please enter a valid mail address');
      }
    }
  };

  return (
    <div>
      {(clientAdministratorsLoading || clientDetailsLoading) && <Spinner />}
      <Header heading={<HeadBack clientName={clientDetails?.name || ''} />} />
      <SwitchBar heading={HeaderOptions} position={5} />

      <div className="max-window-height pb-30 overflow-y-auto pt-7">
        <div className="w-full px-7">
          <p className="text-gray-dark py-2 font-bold">Group Admins</p>
          <hr className="bg-gray-dark"></hr>
        </div>
        <table className="text-md mx-7">
          <thead></thead>
          <tbody>
            {clientAdministrators && clientAdministrators?.length > 0 ? (
              clientAdministrators.map((item: any) => (
                <tr className="w-full px-7">
                  <td className="w-3/12 p-2">
                    {(item.first_name + item.last_name).trim()?.length > 0
                      ? item.first_name + ' ' + item.last_name
                      : 'Name Unavailable'}
                  </td>
                  <td className="w-4/12 p-2">{item.email_address}</td>
                  <td className="w-2/12 p-2">{moment(item.created_at).format('L')}</td>

                  <td className="w-1/12 p-2">
                    {item.client_administrator_role?.status !== 'enabled' && (
                      <button
                        className="rounded-full bg-gray-200 px-4 py-1 text-xs text-gray-900"
                        onClick={() => {
                          resendLink(item.client_administrator_role?.id);
                        }}
                      >
                        RESEND
                      </button>
                    )}
                  </td>
                  <td className="w-1/12 p-2">
                    <button
                      className="rounded-full bg-gray-200 px-4 py-1 text-xs text-gray-900"
                      onClick={() => {
                        setModal({
                          firstName: item.first_name,
                          type: 'remove',
                          id: item.client_administrator_role?.id,
                          emailAddress: item.email_address,
                        });
                      }}
                    >
                      REMOVE
                    </button>
                  </td>
                  <td className="w-1/12 p-2">
                    <button
                      className="rounded-full bg-gray-200 px-4 py-1 text-xs text-gray-900"
                      onClick={() => {
                        setModal({
                          firstName: item.first_name,
                          type: 'revoke',
                          id: item.id,
                          emailAddress: item.email_address,
                        });
                      }}
                    >
                      REVOKE
                    </button>
                  </td>
                  <td className="w-1/12 p-2">
                    <div
                      className={
                        'w-full rounded-md px-4 py-1 text-xs text-white' +
                        (item.client_administrator_role?.status === 'enabled'
                          ? ' stacked-bar-green-primary'
                          : item.client_administrator_role?.status === 'invited'
                            ? ' stacked-bar-orange-primary'
                            : '')
                      }
                    >
                      {item.client_administrator_role?.status.toUpperCase()}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="py-2">No Data Available</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="mt-10 w-full px-7">
          <p className="text-gray-dark py-2 font-bold">Invite Admin</p>
          <hr className="bg-gray-dark"></hr>
        </div>
        <div className="flex w-full items-end px-7 py-4 pb-20">
          <div className="w-5/12">
            <CustomInput
              inputType={'text'}
              value={emailAddress}
              setValue={setEmailAddress}
              Label={'New Group Admin Email'}
              dataTestId="new-group-admin-email"
            />
          </div>
          <div className="px-4">
            <button className="btn-blue w-full rounded-full px-4 py-2 text-xs text-white" onClick={sendInvite}>
              INVITE AS GROUP ADMIN
            </button>
          </div>
        </div>
      </div>

      <Modal
        isModel={modal?.type ? true : false}
        InnerComponent={
          <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
            <div className="bg-white px-4 pb-4 pt-5">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                    Alert
                  </h3>
                  <div className="mt-2 flex">
                    <p className="flex items-center text-sm text-gray-500">
                      {modal?.type &&
                        `${
                          modal.type[0].toUpperCase() + modal.type.slice(1)
                        } User alert - Are you sure you want to ${modal.type} this user from administering ${
                          modal.type === 'revoke' ? `all clients` : `this client`
                        }?`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                onClick={() => {
                  handleAlert(modal!);
                }}
                className="stacked-bar-blue-400 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {modal?.type ? `Yes, ${modal.type[0].toUpperCase() + modal.type.slice(1)}` : ''}
              </button>
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:mt-0 sm:w-auto sm:text-sm"
                onClick={() => {
                  setModal(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        }
      ></Modal>
    </div>
  );
};

export default Admin;
